import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PageShell } from "../components/Layout";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import {
  Package, Copy, ArrowClockwise, CurrencyInr, ShoppingBag, CheckCircle,
  Warning, Funnel, Envelope, Phone as PhoneIcon,
} from "@phosphor-icons/react";

/**
 * Super-Admin · AstroKuberChat Store Orders
 * ──────────────────────────────────────────
 * Lists every gemstone order (paid + pending + failed) with the buyer's
 * full shipping address, phone, email, product SKU, price and Razorpay
 * payment id. Purpose: the founder pastes the shipping details into
 * the courier form manually.
 *
 * Backend: GET /api/super-admin/store/orders?status=paid|created|verification_failed
 */
const STATUS_TABS = [
  { key: "",                   label: "All",           icon: ShoppingBag },
  { key: "paid",               label: "Paid",          icon: CheckCircle },
  { key: "created",            label: "Started · unpaid", icon: Package },
  { key: "verification_failed",label: "Failed",        icon: Warning },
];

export default function AdminStoreOrdersPage() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [orders, setOrders] = useState([]);
  const [summary, setSummary] = useState({ total_orders: 0, paid_orders: 0, revenue_inr: 0 });
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user || !user.is_super_admin) navigate("/");
  }, [user, authLoading, navigate]);

  const fetchOrders = async (s = status) => {
    setLoading(true);
    try {
      const { data } = await api.get("/super-admin/store/orders", { params: s ? { status: s } : {} });
      setOrders(data.orders || []);
      setSummary(data.summary || { total_orders: 0, paid_orders: 0, revenue_inr: 0 });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load orders");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (user?.is_super_admin) fetchOrders(); /* eslint-disable-next-line */ }, [user?.is_super_admin]);

  const copy = async (text, label = "Copied") => {
    try {
      await navigator.clipboard.writeText(String(text || ""));
      toast.success(label);
    } catch {
      toast.error("Clipboard blocked — long-press to copy manually");
    }
  };

  const fullAddress = (o) => [
    o.address_line1,
    o.address_line2,
    `${o.city || ""}${o.state ? ", " + o.state : ""}${o.pincode ? " - " + o.pincode : ""}`,
    o.country,
  ].filter(Boolean).join(", ");

  return (
    <PageShell>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 py-10" data-testid="admin-store-orders-page">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="inline-flex items-center gap-2 text-gold label-eyebrow">
              <Package size={12} weight="fill" /> Super Admin
            </div>
            <h1 className="font-serif-display text-3xl sm:text-4xl mt-2 font-light">
              Store Orders — Gemstones
            </h1>
            <p className="text-muted text-sm mt-2 font-light">
              Every gemstone checkout (paid + pending) with the buyer's shipping details, phone and Razorpay payment id.
            </p>
          </div>
          <button
            onClick={() => fetchOrders()}
            className="rounded-full border border-soft px-4 py-2 text-xs text-muted hover:text-gold hover:border-gold/50 inline-flex items-center gap-1.5"
            data-testid="admin-orders-refresh"
          >
            <ArrowClockwise size={14} /> Refresh
          </button>
        </div>

        {/* Summary */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-6">
          <StatCard label="Total orders"    value={summary.total_orders}  icon={<ShoppingBag size={18} weight="duotone" className="text-gold" />} />
          <StatCard label="Paid orders"     value={summary.paid_orders}   icon={<CheckCircle size={18} weight="duotone" className="text-emerald-400" />} />
          <StatCard label="Revenue (INR)"   value={`₹${Number(summary.revenue_inr || 0).toLocaleString("en-IN")}`} icon={<CurrencyInr size={18} weight="duotone" className="text-gold" />} />
        </div>

        {/* Filter tabs */}
        <div className="flex flex-wrap items-center gap-2 mt-8" data-testid="admin-orders-tabs">
          <Funnel size={14} className="text-muted" />
          {STATUS_TABS.map((t) => (
            <button
              key={t.key || "all"}
              onClick={() => { setStatus(t.key); fetchOrders(t.key); }}
              className={
                "px-3 py-1.5 text-xs rounded-full border inline-flex items-center gap-1.5 transition-colors " +
                (status === t.key
                  ? "bg-gold/20 border-gold/50 text-gold"
                  : "border-soft text-muted hover:text-cream hover:border-gold/30")
              }
              data-testid={`admin-orders-tab-${t.key || "all"}`}
            >
              <t.icon size={12} weight="fill" /> {t.label}
            </button>
          ))}
        </div>

        {/* Orders */}
        <div className="mt-6">
          {loading ? (
            <div className="text-muted text-sm py-10 text-center">Loading orders…</div>
          ) : orders.length === 0 ? (
            <div className="card-surface rounded-md border border-dashed border-soft p-10 text-center text-muted text-sm">
              No orders in this bucket yet.
            </div>
          ) : (
            <div className="space-y-3" data-testid="admin-orders-list">
              {orders.map((o) => (
                <OrderRow
                  key={o.order_id}
                  o={o}
                  open={expanded === o.order_id}
                  onToggle={() => setExpanded(expanded === o.order_id ? null : o.order_id)}
                  onCopy={copy}
                  fullAddress={fullAddress(o)}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </PageShell>
  );
}

function StatCard({ label, value, icon }) {
  return (
    <div className="card-surface rounded-md border border-soft p-4 flex items-center gap-3">
      <div>{icon}</div>
      <div>
        <div className="text-[10px] uppercase tracking-widest text-muted">{label}</div>
        <div className="font-serif-display text-2xl mt-0.5">{value}</div>
      </div>
    </div>
  );
}

function OrderRow({ o, open, onToggle, onCopy, fullAddress }) {
  const created = o.created_at ? new Date(o.created_at) : null;
  return (
    <div className={`card-surface rounded-lg border ${open ? "border-gold/40" : "border-soft"} transition-colors`}
         data-testid={`admin-order-row-${o.order_id}`}>
      <button
        onClick={onToggle}
        className="w-full px-4 py-3 text-left grid grid-cols-1 sm:grid-cols-[1.4fr_1.4fr_1fr_1fr_auto] gap-3 items-center"
      >
        <div>
          <div className="text-sm font-medium text-cream truncate">{o.customer_name || "—"}</div>
          <div className="text-xs text-muted truncate">{o.customer_email || "—"}</div>
        </div>
        <div className="hidden sm:block">
          <div className="text-xs text-cream truncate">{o.product_name}</div>
          <div className="text-[10px] text-muted truncate">SKU {o.sku}</div>
        </div>
        <div className="hidden sm:block text-sm font-medium text-gold">
          ₹{Number(o.amount_inr || 0).toLocaleString("en-IN")}
        </div>
        <div className="hidden sm:block">
          <StatusPill status={o.status} />
        </div>
        <div className="text-[10px] text-muted whitespace-nowrap">
          {created ? created.toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "—"}
        </div>
      </button>

      {open && (
        <div className="border-t border-soft/50 px-4 py-4 grid sm:grid-cols-2 gap-4 text-sm">
          <div>
            <Kv k="Order ID"          v={o.order_id} onCopy={onCopy} mono />
            <Kv k="Razorpay Payment"  v={o.razorpay_payment_id || "—"} onCopy={o.razorpay_payment_id ? onCopy : null} mono />
            <Kv k="Product"           v={`${o.product_name} · ${o.sku}`} />
            <Kv k="Amount"            v={`₹${Number(o.amount_inr || 0).toLocaleString("en-IN")}`} />
            <Kv k="Status"            v={<StatusPill status={o.status} />} />
            <Kv k="Notes from buyer"  v={o.notes || "—"} multiline />
          </div>
          <div>
            <Kv k="Name"      v={o.customer_name} onCopy={onCopy} />
            <Kv k="Phone"     v={o.customer_phone} onCopy={onCopy}
                extra={o.customer_phone && <a href={`tel:${o.customer_phone}`} className="text-gold text-[11px] underline decoration-dotted ml-2"><PhoneIcon size={11} weight="fill" className="inline mr-0.5" />Call</a>} />
            <Kv k="Email"     v={o.customer_email} onCopy={onCopy}
                extra={o.customer_email && <a href={`mailto:${o.customer_email}`} className="text-gold text-[11px] underline decoration-dotted ml-2"><Envelope size={11} weight="fill" className="inline mr-0.5" />Mail</a>} />
            <Kv k="Shipping Address" v={fullAddress || "—"} onCopy={onCopy} multiline />
            <Kv k="Country"   v={o.country || "India"} />
            <Kv k="User ID"   v={o.user_id || "(guest checkout)"} mono />
          </div>
        </div>
      )}
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    paid:                 ["bg-emerald-500/15 text-emerald-400 border-emerald-500/40", "Paid"],
    created:              ["bg-amber-500/15 text-amber-400 border-amber-500/40",   "Unpaid"],
    verification_failed:  ["bg-rose-500/15 text-rose-400 border-rose-500/40",      "Failed"],
  };
  const [cls, label] = map[status] || ["bg-white/5 text-muted border-soft", status || "—"];
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-semibold ${cls}`}>
      {label}
    </span>
  );
}

function Kv({ k, v, onCopy, mono, multiline, extra }) {
  const displayVal = v == null || v === "" ? "—" : v;
  const canCopy = !!onCopy && typeof displayVal !== "object" && displayVal !== "—";
  return (
    <div className={`py-1.5 grid grid-cols-[110px_1fr_auto] gap-2 items-start`}>
      <div className="text-[10px] uppercase tracking-widest text-muted pt-0.5">{k}</div>
      <div className={`${mono ? "font-mono text-xs" : "text-sm"} text-cream ${multiline ? "whitespace-pre-wrap break-words" : "truncate"}`}>
        {displayVal}
        {extra}
      </div>
      {canCopy ? (
        <button onClick={(e) => { e.stopPropagation(); onCopy(String(displayVal), `${k} copied`); }}
                className="text-muted hover:text-gold p-1" aria-label={`Copy ${k}`}>
          <Copy size={12} />
        </button>
      ) : <span />}
    </div>
  );
}
